import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useCoinsGeneration } from './useCoinsGeneration';
import { useCoinMovement } from './useCoinMovement';
import { Coin } from './types';
import { useUser } from '@/hooks/useUser';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';

// ID del juego en la base de datos
const COINS_GAME_ID = '83f64afa-3bdd-4b8f-a9be-57e1faeb0e22';

interface GameState {
  score: number;
  timeLeft: number;
  gameStarted: boolean;
  isGameOver: boolean;
  cerebrixEarned: number;
}

const INITIAL_STATE: GameState = {
  score: 0,
  timeLeft: 5,
  gameStarted: false,
  isGameOver: false,
  cerebrixEarned: 0,
};

const COIN_POINTS = {
  bronze: 5,   // 🥉
  silver: 10,   // 🥈
  gold: 20,     // 🥇
  gem: 40,      // 💎
  chest: 80,   // 🏆
} as const;

const GAME_CONFIG = {
  pointsPerCerebrix: 100, // Puntos necesarios para ganar 1 Cerebrix
};

export const useCoinsGame = () => {
  const [gameState, setGameState] = useState<GameState>(INITIAL_STATE);
  const { coins, setCoins, generateCoins } = useCoinsGeneration();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEndingGame = useRef(false);

  // Iniciar movimiento de monedas
  useCoinMovement(gameState.gameStarted && !gameState.isGameOver, coins, setCoins);

  const endGame = useCallback(async () => {
    if (!user || gameState.isGameOver || isEndingGame.current) return;

    // Prevenir múltiples llamadas
    isEndingGame.current = true;

    try {
      const timeSpent = 5 - gameState.timeLeft;
      const timeBonus = Math.floor(gameState.timeLeft * 0.5);
      const totalScore = gameState.score + timeBonus;

      console.log('Finalizando juego:', {
        timeSpent,
        timeBonus,
        totalScore,
        baseScore: gameState.score,
        gameId: COINS_GAME_ID
      });

      // Verificar que el ID sea válido
      if (!COINS_GAME_ID.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/)) {
        console.error('ID del juego inválido:', COINS_GAME_ID);
        toast({
          title: 'Error',
          description: 'Error de configuración del juego',
          variant: 'destructive'
        });
        return;
      }

      // Registrar la sesión
      const { data: sessionResult, error: sessionError } = await supabase.rpc('register_game_session', {
        p_user_id: user.id,
        p_game_id: COINS_GAME_ID,
        p_points_earned: totalScore,
        p_session_duration: timeSpent,
        p_difficulty_level: 'basic',
        p_bonus_multiplier: 1.0
      });

      if (sessionError) {
        console.error('Error al registrar sesión:', sessionError);
        throw sessionError;
      }

      console.log('Sesión registrada:', sessionResult);

      // Obtener Cerebrix ganados de la sesión
      const cerebrixEarned = sessionResult?.cerebrix_converted || 0;

      // Actualizar estado del juego
      setGameState(prev => ({
        ...prev,
        isGameOver: true,
        cerebrixEarned
      }));

      // Invalidar queries para actualizar la UI
      queryClient.invalidateQueries(['profile']);
      queryClient.invalidateQueries(['cerebrixStats']);

      // Mostrar toast con resultados
      toast({
        title: '¡Juego terminado!',
        description: `¡Ganaste ${totalScore} puntos${cerebrixEarned > 0 ? ` y ${cerebrixEarned} Cerebrix!` : '!'}`,
        className: "bg-green-50 border-2 border-green-500 shadow-lg",
        duration: 5000
      });

    } catch (error) {
      console.error('Error al finalizar el juego:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar tu progreso',
        variant: 'destructive'
      });
    } finally {
      // Asegurarnos de resetear el flag incluso si hay error
      isEndingGame.current = false;
    }
  }, [user, gameState.score, gameState.timeLeft, gameState.isGameOver, queryClient, toast]);

  // Timer del juego
  useEffect(() => {
    if (!gameState.gameStarted || gameState.isGameOver) return;

    const startTime = performance.now();
    const gameDuration = gameState.timeLeft * 1000; // convertir a milisegundos

    const timer = setInterval(() => {
      const currentTime = performance.now();
      const elapsedTime = currentTime - startTime;
      const remainingTime = Math.max(0, gameDuration - elapsedTime);
      const remainingSeconds = Math.ceil(remainingTime / 1000);

      setGameState(prev => {
        if (remainingSeconds <= 0) {
          clearInterval(timer);
          endGame();
          return prev;
        }
        return { ...prev, timeLeft: remainingSeconds };
      });
    }, 100); // actualizar más frecuentemente para mayor precisión

    return () => clearInterval(timer);
  }, [gameState.gameStarted, gameState.isGameOver, endGame]);

  // Verificar si todas las monedas fueron recolectadas
  useEffect(() => {
    if (!gameState.gameStarted || gameState.isGameOver) return;
    
    const allCollected = coins.length > 0 && coins.every(coin => coin.collected);
    if (allCollected) {
      endGame();
    }
  }, [coins, gameState.gameStarted, gameState.isGameOver, endGame]);

  // Iniciar juego
  const startGame = useCallback(() => {
    generateCoins();
    setGameState({
      ...INITIAL_STATE,
      gameStarted: true,
    });
  }, [generateCoins]);

  // Recolectar moneda
  const collectCoin = useCallback((coin: Coin) => {
    if (!gameState.gameStarted || gameState.isGameOver || coin.collected) return;

    setCoins(prevCoins => 
      prevCoins.map(c => 
        c.id === coin.id ? { ...c, collected: true } : c
      )
    );

    const points = COIN_POINTS[coin.type];
    setGameState(prev => ({
      ...prev,
      score: prev.score + points
    }));
  }, [gameState.gameStarted, gameState.isGameOver, setCoins]);

  return {
    coins,
    score: gameState.score,
    timeLeft: gameState.timeLeft,
    gameStarted: gameState.gameStarted,
    isGameOver: gameState.isGameOver,
    cerebrixEarned: gameState.cerebrixEarned,
    startGame,
    collectCoin
  };
};